<template>
  <div class="page">
    <div fluid class="container-fluid main">
      <div class="fixed-header">
        <div class="container">
          <div class="row">
            <div class="col-12 col-md-6 col-lg-7"></div>
            <div class="col-md-12 col-lg-4">
              <Header/>
            </div>
          </div>
        </div>
      </div>
      <div class="content" :class="{ scrolled: scrolled }" @scroll="handleScroll">
        <div class="container" v-if="!isIE">
          <div class="row">
            <div class="col-12 col-md-6 col-lg-7 column-spacer"></div>
            <div class="col-md-12 col-lg-4 primary">
              <div class="form-container">
                <h2 class="heading">Log in</h2>
                <ErrorBox v-if="alert.message" variant="inline" :message="alert.message" /> 
                <form method="post" @submit.prevent="handleSubmit">
                  <div class="form-group">
                    <input
                      type="text"
                      autocomplete="username"
                      v-model="username"
                      id="username"
                      name="username"
                      class="form-control"
                      :class="{ 'is-invalid': submitted && !username }"
                    />
                    <label for="username">Mobile number/Email Address</label>
                    <ValidationFeedback 
                      :show="submitted && !username" 
                      message="Mobile number/Email Address is required" 
                    />
                  </div>
                  <div class="form-group">
                    <div class="password-input-container">
                      <input
                        ref="passwordInput"
                        :type="showPassword ? 'text' : 'password'"
                        autocomplete="current-password"
                        v-model="password"
                        id="password"
                        name="password"
                        class="form-control"
                        :class="{ 'is-invalid': submitted && !password }"
                      />
                      <button 
                        type="button"
                        class="password-toggle-btn"
                        @click="togglePassword"
                      >
                        {{ showPassword ? 'Hide' : 'Show' }}
                      </button>
                       <ValidationFeedback 
                        :show="submitted && !password" 
                        message="Password is required" 
                      />
                    </div>
                    <label for="password">Password</label>
                  </div>
                  <!-- <div class="keep-logged-in">
                    <div class="radio-container">
                      <input
                        type="radio"
                        id="keepLoggedIn"
                        v-model="keepLoggedIn"
                        :value="true"
                        class="custom-radio"
                        @click="toggleKeepLoggedIn"
                      />
                      <label for="keepLoggedIn" class="radio-label">Keep me logged in</label>
                    </div>
                  </div> -->
                  <div class="form-group buttons">
                    <button class="btn btn-submit" :class="{ active: isFormValid }" :disabled="status.loggingIn">
                      <span v-show="!status.loggingIn">Log in</span>
                      <div class="d-flex justify-content-center" v-if="status.loggingIn">
                        <Spinner />
                        <span class="ml-2" v-if="isSlowToLogin">Still busy...</span>
                      </div>
                    </button>
                    <router-link to="reset-password" class="text-link text-right">Forgot Username / Password?</router-link>
                  </div>
                </form>
              </div>
              <div class="footer">
                <div class="cta">
                  <div class="text">
                    <div class="subheading">NETSTAR customers</div>
                  </div>
                  <div class="text register-netstar">
                    <router-link to="register" class="text-link">Register here</router-link>
                  </div>
                </div>
                <div class="info-box">
                  <div>
                    <p>Registered as a security service provider by the Private Security Industry Regulatory Authority, registration number: 1205544.</p>
                    <p class="mb-0">
                      Netstar is a subsidiary of Altron and respects your privacy - please refer to the
                      <a href="https://www.altron.com/privacy-policy/" target="_blank"><u>Altron Privacy Policy</u></a>
                      for details on how we collect and use your personal information, with whom we share it, and your choices and rights in relation to your
                      personal information.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
        <IncompatibleBrowser v-else />
      </div>
    </div>
  </div>
</template>

<style lang="scss" scoped>
@import "~@/assets/scss/pages.scss";

.page {
  .content {
    > .container {
      .secondary {
        .heading {
          margin-bottom: 20px;
        }

        .btn-outline {
          min-width: 210px;
          margin: 0 0 15px 22px;
        }
      }
    }
  }
}

// Password input container styles moved to shared _pages.scss

.keep-logged-in {
  margin-bottom: 15px;
  
  .radio-container {
    display: flex;
    align-items: center;
    gap: 8px;
  }
  
  .custom-radio {
    appearance: none;
    padding: 3px;
    border: 0.12em solid $new-brand-color-1;
    border-radius: 50%;
    cursor: pointer;
    margin: 0;
    color: $new-brand-color-2;

    display: grid;
    place-content: center;
  }

  .custom-radio::before {
    content: "";
    width: 0.8em;
    height: 0.78em;
    border-radius: 50%;
    transform: scale(0);
    transition: 120ms transform ease-in-out;
    box-shadow: inset 1em 1em $new-brand-color-2;
  }

  .custom-radio:checked::before {
    transform: scale(1);
  }
  
  .radio-label {
    font-size: 14px;
    color: $new-brand-color-1;
    cursor: pointer;
    margin: 0;
    line-height: 1;
    display: flex;
    align-items: center;
  }
}

// CTA styles moved to shared _pages.scss
</style>

<script>
import { mapState, mapActions, mapMutations } from "vuex";
import { modalService, publicApiService, tooltipService } from "@/services";
import Header from "@/pages/components/Header";
import IncompatibleBrowser from "@/components/IncompatibleBrowser";
import ErrorBox from "@/components/ErrorBox";
import ValidationFeedback from "@/components/ValidationFeedback";
import { helper } from "@/helper";

export default {
  components: {
    Header,
    IncompatibleBrowser,
    ErrorBox,
    ValidationFeedback,
  },
  beforeRouteEnter(to, from, next) {
    next((vm) => {
      vm.resetErrors();
    });
  },
  beforeRouteUpdate(to, from, next) {
    this.resetErrors();
    next();
  },
  beforeRouteLeave(to, from, next) {
    this.resetErrors();
    next();
  },
  data() {
    return {
      username: "",
      password: "",
      submitted: false,
      scrolled: false,
      isSlowToLogin: false,
      showPassword: false,
      // keepLoggedIn: false,
      loginError: "",
    };
  },
  computed: {
    ...mapState("account", ["status"]),
    ...mapState(["alert"]),
    ...mapState("pwa", ["deferredPromptForInstall", "canInstallApp"]),
    isIE() {
      return /*@cc_on!@*/ false || !!document.documentMode;
    },
    isFormValid() {
      return this.username && this.password;
    },
  },
  created() {
    publicApiService
      .getSystemNotification()
      .then((message) => {
        if (message) {
          modalService.showSystemNotificationDialog(message);
        }
      })
      .catch((error) => {});
  },
  methods: {
    ...mapActions("account", ["login"]),
    ...mapMutations("pwa", ["updateDeferredPromptForInstall"]),
    resetErrors() {
      this.loginError = "";
      this.submitted = false;
      this.isSlowToLogin = false;
      // Clear any global alerts if present
      if (this.$store?.dispatch) {
        this.$store.dispatch("alert/clear");
      }
    },
    promptInstall() {
      if (this.deferredPromptForInstall) {
        this.deferredPromptForInstall.prompt();
        this.deferredPromptForInstall.userChoice.then((choiceResult) => {
          // if (choiceResult.outcome === "accepted") {
          //   console.log("User accepted the install prompt");
          // } else {
          //   console.log("User dismissed the install prompt");
          // }
          this.updateDeferredPromptForInstall(null);
        });
      } else if (this.$isIOS) {
        tooltipService.showTooltip("install-app");
      }
    },
    handleScroll(event) {
      this.scrolled = event.target.scrollTop > 5;
    },
    togglePassword() {
      this.showPassword = !this.showPassword;
      this.$nextTick(() => {
        this.$refs.passwordInput?.focus();
      });
    },
    // toggleKeepLoggedIn() {
    //   this.keepLoggedIn = !this.keepLoggedIn;
    // },
    handleSubmit(e) {
      this.submitted = true;
      this.isSlowToLogin = false;
      this.loginError = ""; // Clear any previous errors
      const { username, password } = this;
      if (username && password) {
        setTimeout(() => {
          this.isSlowToLogin = true;
        }, 4000)
        const origin = helper.getOriginInfo();
        this.login({ username, password, origin })
      }
    },
    togglePasswordVisibility() {
      this.passwordFieldType = this.passwordFieldType === 'password' ? 'text' : 'password';
    },
  },
  watch: {
    username() {
      this.loginError = ""; // Clear error when user starts typing
    },
    password() {
      this.loginError = ""; // Clear error when user starts typing
    },
  },
};
</script>
